import type { WorkspaceRole } from '@server/types';
import { Plus } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import {
  GatewayActions,
  GatewayBrand,
  GatewayContent,
  GatewayRow,
  GatewayShell,
} from '@/_shared/components/GatewayShell';
import { TruncateWithTooltip } from '@/_shared/components/TruncateWithTooltip';
import { Badge } from '@/_shared/components/ui/badge';
import { Button } from '@/_shared/components/ui/button';
import { useAuth } from '@/_shared/hooks/useAuth';
import { usePageTitle } from '@/_shared/hooks/usePageTitle';
import { accessDenialBadge } from '@/_shared/lib/rudderstack-access';

import { PendingInvites } from '../components/pendingInvites/PendingInvites';
import { RegionFlag } from '../components/regionFlag/RegionFlag';

// Mirror the membership badge in Settings → Members (roleBadgeVariant) so the role reads identically
// on both surfaces.
function roleBadgeVariant(role: WorkspaceRole): 'highlight' | 'warning' | 'info' {
  if (role === 'owner') return 'highlight';
  if (role === 'admin') return 'warning';
  return 'info';
}

/**
 * Workspace picker shown when a user lands on a no-workspace URL and belongs to 2+
 * workspaces. Selecting one navigates to `/w/:id{intendedPath}`, preserving the deep
 * path the user originally requested (e.g. `/settings/sources`).
 *
 * A workspace that the RudderStack membership check denies (ACT2-1124 §5.7: suspended, no access, or the check
 * failed) shows a badge instead of the role, and cannot be picked; its tooltip gives the server's reason. The live
 * invites of the person show below the list.
 */
export function WorkspaceSelect({ intendedPath = '' }: { intendedPath?: string }): React.ReactElement {
  usePageTitle('Select Workspace');
  const navigate = useNavigate();
  const { workspaces, signOutAndRedirect } = useAuth();

  return (
    <GatewayShell className="max-w-md">
      <GatewayBrand />
      <GatewayContent title="Select a workspace">
        <div className="flex flex-col gap-2">
          {workspaces.map((ws) => {
            // First line is the Lookout slug; below it the linked RudderStack workspace name followed
            // by a region badge.
            const rsName = ws.rudderstackWorkspace?.name ?? null;
            const region = ws.rudderstackWorkspace?.region ?? null;
            const subtitle =
              rsName || region ? (
                <span className="flex min-w-0 items-center gap-1.5">
                  {rsName && <TruncateWithTooltip>{rsName}</TruncateWithTooltip>}
                  {region && (
                    <Badge variant="outline" className="shrink-0 gap-1">
                      <RegionFlag region={region} />
                      {region}
                    </Badge>
                  )}
                </span>
              ) : undefined;
            const denial = ws.rudderstackAccessDenied ?? null;
            return (
              <GatewayRow
                key={ws.id}
                title={ws.name}
                subtitle={subtitle}
                disabled={denial !== null}
                disabledReason={denial?.message}
                onClick={() => void navigate(`/w/${ws.id}${intendedPath}`, { replace: true })}
                trailing={
                  denial ? (
                    <Badge variant="warning">{accessDenialBadge(denial)}</Badge>
                  ) : (
                    <Badge variant={roleBadgeVariant(ws.role)} className="tracking-wider uppercase">
                      {ws.role}
                    </Badge>
                  )
                }
              />
            );
          })}
        </div>
        <PendingInvites className="mt-5" />
      </GatewayContent>
      <GatewayActions>
        <Button
          variant="secondary"
          size="lg"
          className="w-full gap-2"
          Icon={<Plus />}
          onClick={() => void navigate('/new-workspace')}
        >
          Create workspace
        </Button>
        <Button
          variant="text"
          className="self-center text-xs text-muted-foreground underline-offset-4 hover:text-foreground hover:underline"
          onClick={() => void signOutAndRedirect()}
        >
          Sign in with a different account
        </Button>
      </GatewayActions>
    </GatewayShell>
  );
}
